/**
 * Table displaying file changes with status and line counts.
 */
import type { FC, ReactNode } from "react";

import type { ExecutionFlowNode } from "../../../types";

import { DataTable } from "../../common/DataTable";
import { DocumentIcon } from "../../common/icons";
import { SectionHeader } from "../../common/SectionHeader";
import { StatusBadge } from "../../common/StatusBadge";
import { Text } from "../../common/Text";

type FileChange = NonNullable<ExecutionFlowNode["file_changes"]>[number];

interface FileChangesTableProps {
  fileChanges: FileChange[];
}

const FileChangesAmount: FC<{ file: FileChange }> = ({ file }) => (
  <div className="flex gap-3">
    {file.added_lines != null && (
      <span className="flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
        +{file.added_lines}
      </span>
    )}
    {file.removed_lines != null && (
      <span className="flex items-center gap-0.5 text-rose-600 dark:text-rose-400">
        -{file.removed_lines}
      </span>
    )}
  </div>
);

const MobileField: FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between sm:block">
    <Text variant="label" className="mr-2 sm:hidden">
      {label}
    </Text>
    {value}
  </div>
);

const FileChangeRow: FC<{ file: FileChange }> = ({ file }) => (
  <DataTable.Row className="flex flex-col gap-2 p-3 sm:table-row sm:gap-0 sm:p-0">
    <DataTable.Cell className="w-full break-all font-mono text-xs text-gray-700 sm:px-4 sm:py-3 dark:text-gray-300">
      <MobileField label="Path:" value={<span>{file.path}</span>} />
    </DataTable.Cell>
    <DataTable.Cell className="w-full whitespace-nowrap sm:w-auto sm:px-4 sm:py-3">
      <MobileField
        label="Status:"
        value={<StatusBadge status={file.status} size="sm" withIcon={false} />}
      />
    </DataTable.Cell>
    <DataTable.Cell className="w-full whitespace-nowrap text-right sm:w-auto sm:px-4 sm:py-3">
      <div className="flex items-center justify-between gap-3 font-mono text-xs sm:justify-end">
        <Text variant="label" className="mr-2 sm:hidden">
          Changes:
        </Text>
        <FileChangesAmount file={file} />
      </div>
    </DataTable.Cell>
  </DataTable.Row>
);

export const FileChangesTable: FC<FileChangesTableProps> = ({
  fileChanges,
}) => (
  <div>
    <SectionHeader title="File Changes" icon={<DocumentIcon size={16} />} />
    <div className="overflow-hidden rounded-none border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/50 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
      <DataTable className="min-w-full">
        <DataTable.Header className="hidden sm:table-header-group">
          <tr>
            <DataTable.HeaderCell>Path</DataTable.HeaderCell>
            <DataTable.HeaderCell className="w-32">Status</DataTable.HeaderCell>
            <DataTable.HeaderCell className="w-32 text-right">
              Changes
            </DataTable.HeaderCell>
          </tr>
        </DataTable.Header>
        <DataTable.Body>
          {fileChanges.map((file, idx) => (
            <FileChangeRow key={idx} file={file} />
          ))}
        </DataTable.Body>
      </DataTable>
    </div>
  </div>
);
